<template>
  <div class="terms-page">
    <h1 class="page-title">{{ appName }} User Terms & Conditions</h1>

    <div class="preface">
      Please read the following terms carefully before creating an account and using our services. This agreement defines the rights and obligations between users and the platform, and protects the legitimate interests of all parties.
      You agree to all terms of this agreement once you register, log in or use our products. We reserve the right to revise these terms at any time without prior notice. Your continued use after updates means you accept the revised rules. If you disagree with the changes, please stop using our services immediately.
    </div>

    <div class="term-group">
      <h2 class="group-title">1. Account Rules</h2>
      <div class="group-text">
        You need to register an account to use our services. We may adjust the registration process as needed. By signing up, you agree to enable accounts for our other affiliated services.
        All accounts belong to the platform, and you only have the right to use them. Transfer, sale, rental or gifting of accounts are strictly prohibited.
        You shall take full responsibility for your account and password security. All operations under your account will be regarded as your personal acts. Please contact us promptly if you find unauthorized access.
        We may suspend or terminate your service if you violate any rules. Accounts that remain inactive for one year will be reclaimed or deleted without notice.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">2. Service Scope</h2>
      <div class="group-text">
        Our software provides video chat, in-app purchases, virtual gifts and other functions. We can adjust service contents at any time without notification.
        You authorize us to send commercial messages, advertisements and service reminders via email, SMS, app push and other channels. We may also collect basic information from your mobile device to ensure normal operation.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">3. User Data Protection</h2>
      <div class="group-text">
        You must provide true and compliant personal information during registration and service use. Incomplete or false information will lead to usage restrictions.
        We may collect and use your data to optimize services, and share information with authorized third parties. Please refer to our Privacy Policy for detailed regulations.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">4. Content Usage Rules</h2>
      <div class="group-text">
        All content you create, upload or publish including avatars, nicknames, texts, images, videos and messages shall comply with relevant laws and regulations.
        It is forbidden to publish content that endangers national security, national interests, ethnic unity or public order. Pornography, violence, fraud, insult, harassment and infringement of others' rights are also prohibited.
        You may not develop plug-ins, hacks, conduct reverse engineering, create fake accounts or tamper with our software and data. We will review user content within 24 hours, delete illegal content and ban accounts for serious violations.
        You take full legal responsibility for all content and behaviors you post. You shall compensate for any losses caused to the platform or third parties.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">5. Third-Party Services</h2>
      <div class="group-text">
        Our platform may display third-party advertisements and links. All transactions and use of third-party services are at your own risk. We do not take responsibility for the security and quality of external products.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">6. Membership</h2>
      <div class="group-text">
        You can purchase membership services. Prices and privileges are subject to the official page display. We have the right to adjust membership rules and fees.
        Membership fees are non-refundable and non-transferable in general. Unused service time cannot be extended. You must keep your membership information updated and true.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">7. Subscriptions & Virtual Currency</h2>
      <div class="group-text">
        We offer monthly, two-month and three-month subscription plans. Subscriptions will auto-renew. You need to turn off auto-renewal 24 hours before expiration if you do not need continuous service. All subscription fees cannot be refunded or exchanged for cash.
        Virtual currency such as diamonds is the property of the platform and can only be used for specified functions. We will not offer refunds or compensation for virtual currency due to service changes or failures. Virtual currency will be cleared once your account is terminated.
        Paid services can be purchased through Apple or Google accounts. Cancellation of subscriptions must be done on the corresponding third-party platform.
        We may terminate your membership at any time without refund for violations or arrears. Mobile communication fees shall be borne by users.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">8. Data Storage</h2>
      <div class="group-text">
        We do not guarantee permanent storage of your data. We will set storage duration and capacity by ourselves. Please back up your important data regularly.
        After you stop using the service, we may permanently delete your data from servers. You may edit or delete your personal content, and you shall bear the corresponding risks.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">9. Risk Notice</h2>
      <div class="group-text">
        This platform is only an information exchange platform. You shall bear all risks and liabilities for your account operations and published content.
        Please report any improper use to us in a timely manner. We reserve the right to change, suspend or close any service functions at any time.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">10. Intellectual Property</h2>
      <div class="group-text">
        All intellectual property rights of this software, logos and related services belong to us. You own the copyright of your uploaded content, but you grant us permanent, free, worldwide authorization to use, edit and distribute the content for any purpose.
        Unauthorized use, modification or secondary creation of our intellectual property is prohibited.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">11. Liability Provisions</h2>
      <div class="group-text">
        For your illegal or breach-of-agreement behaviors, we may issue warnings, restrict functions, ban accounts, deduct virtual assets and take other measures.
        You shall bear all losses, compensation and legal expenses arising from your violations. We may take legal action and report to relevant authorities.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">12. Force Majeure & Disclaimer</h2>
      <div class="group-text">
        We are not liable for service interruptions caused by natural disasters, wars, policy changes and other uncontrollable factors.
        We do not guarantee that the service will be always stable, safe and error-free. We are not responsible for losses caused by network faults, viruses, hacker attacks, bad user content or offline transactions between users.
        We are not responsible for free products and services. Our total compensation shall not exceed the fees you have paid.
        <br /><br />
      </div>
    </div>

    <div class="term-group">
      <h2 class="group-title">13. Miscellaneous</h2>
      <div class="group-text">
        Please read these terms carefully. Minors shall read them with their guardians. All rules released in the software are supplementary agreements with equal legal effect.
        This agreement is governed by the laws of the Hong Kong Special Administrative Region. All disputes shall be resolved by arbitration at the Hong Kong International Arbitration Centre.
        If any clause is invalid, the rest shall still take effect.
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      appName: ""
    };
  },
  created() {}
};
</script>

<style scoped>
/* 整体页面样式：简约白底，线条分割风格，全新视觉 */
.terms-page {
  max-width: 940px;
  margin: 36px auto;
  padding: 0 20px;
  font-family: 'Arial', 'Times New Roman', sans-serif;
  background: #ffffff;
}

/* 页面主标题 */
.page-title {
  font-size: 27px;
  color: #111827;
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 28px;
  font-weight: 700;
}

/* 前言部分 */
.preface {
  font-size: 16px;
  line-height: 1.8;
  color: #374151;
  margin-bottom: 32px;
  padding: 0 8px;
}

/* 单个条款区块 */
.term-group {
  margin-bottom: 26px;
  padding: 12px 16px;
  border-left: 3px solid #3b82f6;
  background-color: #f8fafc;
}

/* 区块标题 */
.group-title {
  font-size: 18px;
  color: #1e40af;
  margin: 0 0 10px 0;
  font-weight: 600;
}

/* 区块正文 */
.group-text {
  font-size: 15px;
  line-height: 1.75;
  color: #4b5563;
  margin: 0;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .terms-page {
    margin: 20px auto;
  }
  .page-title {
    font-size: 23px;
  }
  .preface, .group-text {
    font-size: 14px;
  }
  .group-title {
    font-size: 16px;
  }
  .term-group {
    padding: 10px 12px;
  }
}
</style>